import React from 'react';
import { 
  BrowserRouter as Router,
  Navigate,
  Outlet,
  useLocation,
  Route,
  Routes
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BalanceProvider } from './context/BalanceContext';
import { PriceProvider } from './context/PriceContext';
import { ThemeProvider } from './theme/ThemeProvider';
import { NotificationProvider } from './contexts/NotificationContext';

import LandingPage from './pages/LandingPage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import About from './pages/About';
import Exchange from './pages/Exchange';
import ContractTrading from './pages/ContractTrading';
import CoinDetails from './pages/CoinDetails';
import ArbitrageTrading from './pages/ArbitrageTrading';
import GlassDockNav from './components/GlassDockNav';

// Protects routes with loading state
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-cyan-300">Loading...</div>
      </div>
    );
  }
  
  if (!currentUser) {
    // Redirect to signin page, but save the current location they were trying to go to
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }
  
  return children;
};

// Create a layout component that includes SideDrawerNav
const Layout = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const showNav = !['/signin', '/signup', '/'].includes(location.pathname) && currentUser;
  
  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1 pb-24 sm:pb-16">
        <Outlet />
      </main>
      {showNav && <GlassDockNav />}
    </div>
  );
};

// Main router component with authentication
const AppRouter = () => {
  const { currentUser, userData, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-cyan-300">Loading application...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<About />} />
        
        {/* Authentication Routes */}
        <Route 
          path="/signin" 
          element={!currentUser ? <SignInPage /> : <Navigate to="/dashboard" replace />} 
        />
        <Route 
          path="/signup" 
          element={!currentUser ? <SignUpPage /> : <Navigate to="/dashboard" replace />} 
        />
        
        {/* Protected Routes */}
        <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/exchange" element={<Exchange />} />
          <Route path="/contract-trading" element={<ContractTrading />} />
          <Route path="/trade/:symbol" element={<ContractTrading />} />
          <Route path="/trading" element={<ContractTrading />} />
          <Route path="/coin/:id" element={<CoinDetails />} />
          <Route path="/arbitrage" element={<ArbitrageTrading />} />

        </Route>
        
        {/* Fallback Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <AuthProvider>
          <PriceProvider>
            <BalanceProvider>
              <NotificationProvider>
                <div className="min-h-screen w-full bg-gradient-to-br from-navy via-navyLight to-slate-950 text-slate-50">
                  <AppRouter />
                </div>
              </NotificationProvider>
            </BalanceProvider>
          </PriceProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
